import { TsDOM as q, debounce } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class SearchDrawer extends UcoastEl {
    static htmlSelector = 'search-drawer'
    static selectors = {
        overlay: '[data-uc-search-drawer-overlay]',
        searchLink: '#SearchIconBubble',
        searchInput: '.search-drawer__input',
        seachIcon: '.icon-search',
        container: '#SearchDrawer',
        instantSearchContainer: '.instant-search',
        searchResultsContainer: '.search-results',
        allResultsButton: '.search-drawer__all-results',
        clearButton: '.search-drawer__input-clear'
    }
    searchInput: HTMLInputElement
    seachIcon: HTMLElement
    instantSearchContainer: HTMLElement
    searchResultsContainer: HTMLElement
    clearButton: HTMLElement
    activeElement?: HTMLElement
    abortController: AbortController
    constructor() {
        super()

        this.searchInput = q.rs(SearchDrawer.selectors.searchInput, this)
        this.seachIcon = q.rs(SearchDrawer.selectors.seachIcon, this)
        this.instantSearchContainer = q.rs(SearchDrawer.selectors.instantSearchContainer, this)
        this.searchResultsContainer = q.rs(SearchDrawer.selectors.searchResultsContainer, this)
        this.clearButton = q.rs(SearchDrawer.selectors.clearButton, this)
        this.abortController = new AbortController()

        this.maybeAutoOpen()
    }

    override connectedCallback(): void {
        this.addEventListener(
            'keyup',
            (evt) => evt.code === 'Escape' && this.close()
        )
        this.getOverlay().addEventListener('click', this.close.bind(this))
        this.setHeaderSearchIconAccessibility()
        this.searchInput.addEventListener('keyup', this.onKeyupSearchInput.bind(this))
        this.searchInput.addEventListener(
			'input',
			debounce((_event: Event) => {
				this.onInputSearchInput()
			}, 300).bind(this)
		)
        this.seachIcon.addEventListener('click', this.redirectSearchPage.bind(this))
        this.clearButton.addEventListener('click', this.resetForm.bind(this))
    }

    getOverlay() {
        return q.rs(SearchDrawer.selectors.overlay, this)
    }

    maybeAutoOpen() {
		const url = new URL(window.location.href)
		const urlParams = url.searchParams
		if (urlParams.get('open') === 'search') {
			this.open()
		}
	}

    setHeaderSearchIconAccessibility() {
        const searchLink = q.rs(SearchDrawer.selectors.searchLink)
        searchLink.setAttribute('role', 'button')
        searchLink.setAttribute('aria-haspopup', 'dialog')
        searchLink.addEventListener('click', (event) => {
            event.preventDefault()
            this.open(searchLink)
        })
        searchLink.addEventListener('keydown', (event) => {
            if (event.code?.toUpperCase() === 'SPACE') {
                event.preventDefault()
                this.open(searchLink)
            }
        })
    }

    open(triggeredBy?: HTMLElement) {
        if (triggeredBy) this.setActiveElement(triggeredBy)
        setTimeout(() => {
            this.classList.add('animate', 'active')
        }, 1)
        this.addEventListener(
            'transitionend',
            () => {
                void window.Ucoast.mediaManager.playAllInContainer(this)
                const containerToTrapFocusOn = q.rs(SearchDrawer.selectors.container, this)
                const focusElement = q.rs(SearchDrawer.selectors.searchInput, this)
                window.TsDOM.trapFocus(containerToTrapFocusOn, focusElement)
            },
            { once: true }
        )
        document.body.classList.add('overflow-hidden')
    }

    close() {
        this.classList.remove('active')
        window.TsDOM.removeTrapFocus(this.activeElement)
        document.body.classList.remove('overflow-hidden')
    }

    setActiveElement(element: Element | null) {
        if (!element) throw new Error('Active element is required')
        this.activeElement = element as HTMLElement
    }

    getQuery() {
		return this.searchInput.value.trim()
	}

    onKeyupSearchInput(e: KeyboardEvent) {
        if(e.code == 'Enter') this.redirectSearchPage()
    }

    renderSearchResult(searchResult: HTMLElement | null) {
        this.searchResultsContainer.innerHTML = '';
        if (searchResult) {
          this.searchResultsContainer.append(...searchResult.childNodes);
        }
        this.searchResultsContainer.classList.remove('hidden')
        if(!this.searchResultsContainer.querySelector('.search-drawer__no-results')) {
            this.instantSearchContainer.classList.add('hidden')
            this.classList.remove('no-result')
        } else {
            this.instantSearchContainer.classList.remove('hidden')
            this.classList.add('no-result')
        }
    }

    resetForm() {
        this.clearButton.classList.add('hidden')
        this.searchInput.value = ''
        this.searchResultsContainer.innerHTML = ''
        this.instantSearchContainer.classList.remove('hidden')
        this.searchResultsContainer.classList.add('hidden')
        this.classList.remove('no-result')
        this.abortController.abort()
        this.abortController = new AbortController()
    }

    async onInputSearchInput() {
        try {
            const q = this.getQuery()
            if(q) {
                this.clearButton.classList.remove('hidden')
                const searchTemplate = await fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=4&resources[limit_scope]=each`,
                {
                    signal: this.abortController.signal,
                });
                const responseText = await searchTemplate.text()
                const predictiveSearch = new DOMParser().parseFromString(responseText, 'text/html').getElementById('shopify-section-predictive-search')
                this.renderSearchResult(predictiveSearch)
            } else {
                this.resetForm()
            }
        } catch (error) {
            console.error('Error fetching search results:', error);
        }
    }

    redirectSearchPage() {
        const q = this.getQuery()
        if(!q) return
        window.location.href = `/search?q=${q}&type=product`
    }
}